import React, { useState, useEffect } from 'react'
import Box from '@mui/material/Box'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import ButtonInc from '../../../shared-ui/ButtonInc'
import {
  POLICY_STATUSES,
  formatPolicyStatusLabel
} from '../../../lib/policyStatusEnum.es6'

const BENEFICIARY_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'missing', label: 'Not captured' }
]

// Date fields the from/to range can apply to. due_date is rejected by the
// endpoint's date_discriminator enum today (HTTP 400); the backend team is
// adding it — the option ships ahead of that fix per product decision.
const DATE_DISCRIMINATORS = [
  { value: 'start_date', label: 'Start Date' },
  { value: 'due_date', label: 'Due Date' }
]

const EMPTY_INPUTS = {
  status: '',
  dateBy: 'start_date',
  fromDate: '',
  toDate: '',
  beneficiary: ''
}

// Default view: policies whose start_date falls in the last 30 days
// (open-ended From, no To — the endpoint accepts one-sided ranges).
export function defaultSambaInputs() {
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
  return {
    ...EMPTY_INPUTS,
    fromDate: thirtyDaysAgo.toISOString().split('T')[0]
  }
}

// Convert the filter inputs into /api/policies/complex/filter body params.
// The date range is sent as UTC day bounds gated to the date field chosen
// in the discriminator dropdown. "Not captured" matches
// additional_details.next_of_kin_national_id null — samba's beneficiary
// capture key and the only beneficiary field the endpoint honors as a
// filter (the top-level beneficiary_* columns are stripped by its DTO;
// verified against staging).
export function buildSambaFilterParams(inputs) {
  const params = {}
  if (inputs.status) {
    params.status = inputs.status
  }
  if (inputs.fromDate || inputs.toDate) {
    if (inputs.fromDate) {
      params.from = `${inputs.fromDate}T00:00:00.000Z`
    }
    if (inputs.toDate) {
      params.to = `${inputs.toDate}T23:59:59.999Z`
    }
    params.date_discriminator = inputs.dateBy
  }
  if (inputs.beneficiary === 'missing') {
    params.additional_details = { next_of_kin_national_id: null }
  }
  return params
}

const SambaPolicyFilters = props => {
  const [inputs, setInputs] = useState(defaultSambaInputs)
  const [searchInput, setSearchInput] = useState(props.searchValue || '')

  useEffect(function() {
    setSearchInput(props.searchValue || '')
  }, [props.searchValue])

  const setField = field => event => {
    setInputs({ ...inputs, [field]: event.target.value })
  }

  const invalidRange =
    !!inputs.fromDate && !!inputs.toDate && inputs.fromDate > inputs.toDate
  const dateHelperText = invalidRange
    ? 'From date must be on or before To date'
    : ''

  const handleApply = () => {
    props.onApply(buildSambaFilterParams(inputs))
  }

  const handleClear = () => {
    setInputs(EMPTY_INPUTS)
    props.onApply({})
  }

  const applyDisabled = invalidRange || props.disabled

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1.5,
        alignItems: 'flex-start',
        mb: 2.5
      }}
    >
      <TextField
        select
        size="small"
        label="Policy Status"
        value={inputs.status}
        onChange={setField('status')}
        sx={{ flex: 1, minWidth: 140 }}
      >
        <MenuItem value="">All Statuses</MenuItem>
        {POLICY_STATUSES.map(status => (
          <MenuItem key={status} value={status}>
            {formatPolicyStatusLabel(status)}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        size="small"
        label="Filter Date By"
        value={inputs.dateBy}
        onChange={setField('dateBy')}
        sx={{ flex: 1, minWidth: 120 }}
      >
        {DATE_DISCRIMINATORS.map(option => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        size="small"
        type="date"
        label="From Date"
        value={inputs.fromDate}
        onChange={setField('fromDate')}
        error={invalidRange}
        InputLabelProps={{ shrink: true }}
        sx={{ flex: 1, minWidth: 140 }}
      />

      <TextField
        size="small"
        type="date"
        label="To Date"
        value={inputs.toDate}
        onChange={setField('toDate')}
        error={invalidRange}
        helperText={dateHelperText}
        InputLabelProps={{ shrink: true }}
        sx={{ flex: 1, minWidth: 140 }}
      />

      <TextField
        select
        size="small"
        label="Beneficiary Details"
        value={inputs.beneficiary}
        onChange={setField('beneficiary')}
        sx={{ flex: 1, minWidth: 140 }}
      >
        {BENEFICIARY_OPTIONS.map(option => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      <ButtonInc
        variant="contained"
        onClick={handleApply}
        disabled={applyDisabled}
        sx={{ textTransform: 'none' }}
      >
        Apply Filters
      </ButtonInc>

      <ButtonInc
        variant="outlined"
        onClick={handleClear}
        disabled={props.disabled}
        sx={{ textTransform: 'none' }}
      >
        Clear
      </ButtonInc>

      <Box sx={{ flexBasis: '100%', display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 0.5 }}>
        <TextField
          size="small"
          placeholder="Search by policy number"
          value={searchInput}
          onChange={function(e) { setSearchInput(e.target.value) }}
          onKeyDown={function(e) { if (e.key === 'Enter') props.onSearch(searchInput) }}
          sx={{ flex: 1, maxWidth: 320 }}
        />
        <ButtonInc
          variant="contained"
          onClick={function() { props.onSearch(searchInput) }}
          disabled={props.disabled || !searchInput}
          sx={{ textTransform: 'none' }}
        >
          Search
        </ButtonInc>
      </Box>
    </Box>
  )
}

export default SambaPolicyFilters
